import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from "react-router-dom";
import get from "lodash/get";

import ChatBotMessages from '../../../components/chatbot/ChatBotMessages';
import { chatBotSendMessage, chatBotSendMessageCancelToken, chatBotVisualizeMessage, getConversationMessage, resetChatBot } from '../../../actions/chatbot/common';

import { isRestrictedPath } from '../../../utils/chatbotHelper';
import { genericErrorText, screenBreakpoints } from '../../../utils/commonVariables';
import Loader from '../../../components/misc/Loader';

import MessageHeader from '../../../components/chatbot/MessageHeader';
import WelcomeScreen from '../../../components/chatbot/WelcomeScreen';
import Header from '../../../components/chatbot/Header';
import Input from '../../../components/chatbot/Input';
import NoData from '../../../components/chatbot/NoData';
import CompanyHeader from '../../../components/chatbot/CompanyHeader';

const ChatBot = (props) => {
    const { view, user: { user }, token, getConversationMessage, resetChatBot, chatBotVisualizeMessage } = props;
    const { conversation_id, context_list, company_list, data, session: {
        fetching, error
    } } = view;
    const [value, setValue] = useState('');
    const [isMinimized, setIsMinimzed] = useState(false);
    const [conversationtUI, setConversationUI] = useState(false);
    const [moduleItem, setModuleItem] = useState(null);
    const [company, setCompany] = useState(null);
    const messages = get(view, "data", []);
    const isAnyMesssageTyping = messages.find(each => each.isTyping);
    const isAnyMesssageFetching = messages.find(each => each.fetching);
    const location = useLocation();
    const chatBotToShow = get(user, "profile.ai_enabled", false);
    const chatBotUrl = get(user, "profile.ai_url", false);
    const isMobile = window.innerWidth <= screenBreakpoints.mob;
    const chatRestrictedPaths = isRestrictedPath(location.pathname);
    const disabled = !!isAnyMesssageTyping || !value || !!isAnyMesssageFetching || !moduleItem;

    const handleChange = (val) => {
        setValue(val);
    };
    const handleCompanyChange = (company) => {
        setCompany(company);
        setValue("");
    };
    const toggleMinimize = () => {
        setIsMinimzed(prev => {
            const newValue = !prev;
            document.body.style.overflow = newValue ? 'auto' : 'hidden';
            return newValue;
        });
        setValue("");
    }

    const scrollPanelToBottom = () => {
        setTimeout(() => {
            const messagePanel = document.querySelector('.chatbot__messages');
            if (messagePanel) {
                messagePanel.scrollTo({
                    top: messagePanel.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 0);
    }
    const handleSubmit = (id) => {
        scrollPanelToBottom();
        const data = {
            conversation_id: conversation_id,
            message: value,
            counter: messages ? (messages.filter(item => item.isSelf).length + 1) : 1,
            module_id: id ? id : moduleItem.module_id,
            module_title: moduleItem && moduleItem.title,
            module_name: moduleItem && moduleItem.module_name,
            report_id: moduleItem && moduleItem.id,
            company_id: moduleItem && moduleItem.company_id,
            report_title: moduleItem && moduleItem.title,
        }
        props.chatBotSendMessage(data, token, value, chatBotUrl).then(res => {
            const isError = get(res, "errors.status", false);
            if (!res && !!isError) {
                const errorText = get(res, "errors.display_message", genericErrorText.general);
                alert(errorText);
                return false;
            }
        });
        setValue("");
    };

    const handleVisualizeData = () => {
        scrollPanelToBottom();
        const lastTwoMessages = messages && messages.slice(-2);
        const userQuestion = lastTwoMessages[0] ? lastTwoMessages[0].message : "";
        const textToVisualize = lastTwoMessages[1] ? lastTwoMessages[1].message : "";

        const data = {
            conversation_id: conversation_id,
            message: "Show data visually", // This is the static meesage for user
            counter: messages ? (messages.filter(item => item.isSelf).length + 1) : 1,
            module_id: moduleItem && moduleItem.module_id,
            module_title: moduleItem && moduleItem.title,
            module_name: moduleItem && moduleItem.module_name,
            text_to_visualize: textToVisualize, // This is the text to visualize (response from chatbot)
            user_question: userQuestion, // This is the user question (user input)
        }
        chatBotVisualizeMessage(data, token).then(res => {
            const isError = get(res, "errors.status", false);
            if (!res && !!isError) {
                const errorText = get(res, "errors.display_message", genericErrorText.general);
                alert(errorText);
                return false;
            }
        });

    }

    const handleResetContext = () => {
        setModuleItem(null);
        setValue("");
        resetChatBot();
        getConversationMessage(token);
        setConversationUI(false);
        setCompany(null);
        if (chatBotSendMessageCancelToken) {
            chatBotSendMessageCancelToken();
        }
    }

    const handleSubmitContext = (item) => {
        if (value && company) {
            handleSubmit(item.module_id);
        }
        setModuleItem(item);
        setConversationUI(true);
        setValue("");
    }

    useEffect(() => {
        if (chatBotToShow) {
            getConversationMessage(token, chatBotUrl);
        };
    }, [chatBotToShow]);

    useEffect(() => {
        setIsMinimzed(true);
        setValue("");
        document.body.style.overflow = 'auto';
    }, [location]);

    // If the chatbot is not enabled for the user or if the path is restricted, return null to avoid rendering.
    if (!chatBotToShow || !!chatRestrictedPaths) return null;
    return (
        <div className={`chatbot ${isMinimized ? 'minimized' : ''}`}>
            <div
                className={`chatbot__content ${isMobile ? "mobile" : ""} 
            ${(error || fetching) ? "empty-session" :
                        conversationtUI ? 'conversation' : 'pre-conversation'} 
            ${!conversationtUI && company ? 'company-header' : ''}`}
            >
                <Header isMinimized={isMinimized} toggleMinimize={toggleMinimize} />
                {
                    !!error ? <NoData /> :
                        !!fetching ? <Loader isCentered withMargin isBothCentered /> :
                            <>

                                {/* Header - Shows Currently Selected Company & Report in Chat Window */}
                                {!!conversationtUI &&
                                    <MessageHeader
                                        handleClick={handleResetContext}
                                        moduleItem={moduleItem}
                                    />}

                                {/* Messages or Company Header Component Until Chat Window Opened */}
                                {(!conversationtUI && company) ?
                                    <CompanyHeader
                                        company={company}
                                        handleCompanyChange={handleCompanyChange}
                                    />
                                    : <ChatBotMessages
                                        messages={messages}
                                        isAnyMesssageTyping={isAnyMesssageTyping}
                                        handleVisualizeData={handleVisualizeData}
                                    />}
                                {/* Text Input UI for Search Companies or Chat Window Input  */}
                                <Input
                                    value={value}
                                    handleChange={handleChange}
                                    handleChatSubmit={handleSubmit}
                                    conversationtUI={conversationtUI}
                                    disabled={disabled}
                                    company={company}
                                />

                                {/* Welcome Screen - Companis & Report Selection List */}
                                {!conversationtUI &&
                                    <WelcomeScreen
                                        handleSubmitContext={handleSubmitContext}
                                        list={context_list}
                                        companyList={company_list}
                                        value={value}
                                        handleCompanyChange={handleCompanyChange}
                                        company={company}
                                    />
                                }
                            </>
                }
            </div>
        </div>
    )
}

function mapStateToProps(state) {
    return {
        token: state.login.token,
        view: state.chatBotReducer.view,
        user: state.user
    }
}

const mapDispatchToProps = {
    getConversationMessage,
    chatBotSendMessage,
    chatBotVisualizeMessage,
    resetChatBot
}
export default connect(mapStateToProps, mapDispatchToProps)(ChatBot);